import {
	Box,
	Group,
	MantineSpacing,
	Stack,
	StyleProp,
	Text
} from "@mantine/core"
import {
	FC,
	ReactNode,
	Children,
	isValidElement,
	useMemo,
	useEffect
} from "react"
import styles from "./CustomStepper.module.css"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { stylizeText } from "@utils/stylizeText"

interface CustomStepperProps {
	active: number
	onStepClick?: (stepIndex: number) => void
	onComplete?: () => void
	children: ReactNode
	allowNavigation?: boolean
	stepGap?: number
	p?: StyleProp<MantineSpacing>
	hasExtraPadding?: boolean
}
interface StepProps {
	children: ReactNode
}

const CustomStepper: FC<CustomStepperProps> & {
	Step: FC<StepProps>
} = ({
	active,
	onStepClick,
	onComplete,
	children,
	allowNavigation = false,
	stepGap = 4,
	p,
	hasExtraPadding = false
}) => {
	const childrenArray = useMemo(() => Children.toArray(children), [children])
	const steps = useMemo(
		() => childrenArray.filter((child) => isValidElement(child)),
		[childrenArray]
	)

	const stepCount = useMemo(() => steps.length, [steps])
	const isCompleted = useMemo(
		() => active >= steps.length,
		[active, steps.length]
	)
	useEffect(() => {
		if (isCompleted && onComplete) {
			onComplete()
		}
	}, [isCompleted, onComplete])

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: "smooth" })
	}, [active])

	const renderStepBars = useMemo(
		() => (
			<Group gap={0} justify="space-between" w="100%" mb={18}>
				{steps.map((_, index) => (
					<Box
						w={`calc((100% / ${stepCount}) - ${stepGap}px)`}
						key={index}
						className={`${styles.stepBar} ${
							index < active
								? styles.completed
								: index === active
									? styles.active
									: styles.inactive
						}`}
						onClick={() => allowNavigation && onStepClick?.(index)}
						style={{ cursor: allowNavigation ? "pointer" : "default" }}
					/>
				))}
			</Group>
		),
		[steps, stepCount, active, allowNavigation, onStepClick, stepGap]
	)

	const renderActiveStep = useMemo(() => {
		const activeStepComponent = steps[active]
		return activeStepComponent || null
	}, [steps, active])
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	return (
		<Stack
			pl={hasExtraPadding ? { base: 20, lg: 65 } : 0}
			pr={hasExtraPadding ? { base: 20, lg: 65 } : 0}
			align="center"
			justify="center"
			mih={"100%"}
			flex={1}
		>
			<Stack w="100%" p={p} gap={0} maw={525} mih={"100%"}>
				{renderStepBars}
				<Text
					m={0}
					fz={14}
					lh={1.5}
					ta="left"
					fw={600}
					c={"var(--mantine-color-royal-8"}
				>
					{stylizeText(dictionary?.paginationSteps ?? "", {
						fz: { base: 14 },
						fw: 600,
						variables: [`${active + 1}`, `${stepCount}`]
					})}
				</Text>
			</Stack>

			<Stack w="100%" mih="100%" flex={1}>
				{renderActiveStep}
			</Stack>
		</Stack>
	)
}

const Step: FC<StepProps> = ({ children }) => {
	return <>{children}</>
}
CustomStepper.Step = Step

export default CustomStepper
